import React, { useState } from 'react';
import {
  DataTitle,
  DataValue,
  NoData,
  Poppins,
  Settings,
  Spacer,
  TitleWithBack,
  getIncompleteStateNew,
  IncompleteStateMarkerNew,
} from 'src/common';
import CommonMenu from 'src/components/CommonMenu';
import styled from 'styled-components';
import { useNavigate, useParams } from 'react-router-dom';
import { useScenario } from 'src/state/scenario';
import { queryCache } from 'react-query';
import { getScenarioStatusName, QKeys } from 'src/api/types';
import { VersionHeader } from 'src/hooks/useVersion/comps';
import {
  ConfirmDeleteModal,
  LinkControlsToScenarioModal,
  PdfExportModalScenario,
  ScenarioModal,
  VariablesWorkspaceModal,
} from 'src/components/modals';
import Comments from 'src/components/Comments';
import { mpEvent } from 'src/utils/mixpanel/useMixPanel';
import { MPEvents } from 'src/utils/mixpanel/types';
import { TLLink } from 'src/pages/DashboardPage/comps';
import { formatDate } from 'src/utils/misc';
import { AiSuggest } from './AiSuggest';
import { useStateSelector } from 'src/redux';
import useActionsApp from 'src/redux/actionHooks/useActionsApp';
import QuickActionsMenu from 'src/components/QuickActionsMenu';
import { StyledSandboxModal, ScenarioView } from 'src/components/ProjectedRisk';
import { isBefore } from 'date-fns';
import { ObjectKind, ScenarioQuery, useDeleteScenarioMutation, VersionsQuery } from 'src/graphql/_generated';
import { useCache } from 'src/graphql';
import { useApolloClient } from '@apollo/client';
import { showSuccessToast, toastMessages } from 'src/components/Toast';
import InfoHint from 'src/components/InfoHint';
import { LABELS } from 'src/components/labels';

const Div = styled.div`
  display: flex;
  justify-content: space-between;

  .head-end {
    display: flex;
    align-items: flex-end;
    justify-content: center;
    flex-direction: column;

    &__controls {
      display: flex;
      grid-gap: 20px;
      gap: 20px;
      align-items: center;
    }

    &__loss {
      display: flex;
      align-items: center;
      position: relative;
      grid-gap: 8px;
      gap: 8px;
    }
  }
`;

const Theader = styled.div`
  .h-title-container {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    grid-gap: 10px;
    gap: 10px;
  }

  .meta {
    display: grid;
    grid-template-columns: auto auto auto auto 1fr;
    grid-gap: 20px;
    &__loss {
      justify-content: flex-end;
    }
  }
  .sub-header {
    display: flex;
    align-items: center;
    grid-gap: 8px;
  }

  ${({ theme }) => theme.breakpoints.down('md')} {
    .meta {
      grid-gap: 4px;
      grid-row-gap: 5px;
      &__loss {
        grid-row: 2;
        justify-content: flex-start;
      }
    }
  }
`;

interface HeadProps {
  isLogarithmic: boolean;
  scenario: NonNullable<ScenarioQuery['scenario']>;
  expectedLossComponent: React.ReactNode;
  version: NonNullable<VersionsQuery['assessment']>['versions'][number] | undefined;
}

export const Head: React.FC<HeadProps> = ({ isLogarithmic, scenario, expectedLossComponent, version }) => {
  const { scenarioId, assessmentId } = useParams<{ scenarioId: string; assessmentId: string }>();
  const isLinkControlsToScenarioModalOpen = useStateSelector(({ app }) => app.modals.isLinkControlsToScenarioModalOpen);
  const { isOpen, openModal, closeModal } = useScenario();
  const [anchorEl, setAnchorEl] = useState<React.MouseEvent['currentTarget'] | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isVariablesModalOpen, setIsVariablesModalOpen] = useState(false);
  const [isExportPdfModalOpen, setIsExportPdfModalOpen] = useState(false);
  const [isSandboxModalOpen, setIsSandboxModalOpen] = useState(false);
  const [isCommentsModalOpen, setIsCommentsModalOpen] = useState(false);

  const { setLinkControlsToScenarioModal } = useActionsApp();

  const hasControls = scenario.riskManagementPlans?.length > 0;

  const navigate = useNavigate();

  const { refetchQuery } = useCache();
  const client = useApolloClient();

  const [remove] = useDeleteScenarioMutation();

  const handleClose = () => setAnchorEl(null);

  const onMenuClick = (e: React.MouseEvent<HTMLLIElement>, cb: () => void) => {
    e.stopPropagation();
    handleClose();
    cb();
  };

  const handleOnDeleteClick = async () => {
    await remove({ variables: { id: scenarioId! } });
    mpEvent(MPEvents.ScenarioDelete, { id: scenario.id, tags: ['SCENARIO'] });
    client.cache.evict({ id: client.cache.identify({ __typename: 'Scenario', id: scenarioId }) });
    client.cache.gc();
    queryCache.invalidateQueries([QKeys.Audit]);
    refetchQuery({ include: ['assessment', 'assessmentRiskRegister'] });
    showSuccessToast(toastMessages.scenario.deleted);
    navigate(`/risk-assessments/${assessmentId}`);
  };

  const incompleteState = getIncompleteStateNew(scenario);

  const projectedRiskTooltipText = version
    ? 'Sandbox unavailable for this version.'
    : hasControls && incompleteState !== 'TECH'
      ? ''
      : incompleteState !== 'TECH'
        ? 'Linked controls are required for sandbox.'
        : 'Please adjust the scenario to contain valid calculation data.';

  const isSuggestionButtonReadOnly = !!scenario.riskManagementPlans?.length;

  return (
    <>
      {isCommentsModalOpen && (
        <Comments
          onClose={() => setIsCommentsModalOpen(false)}
          eventData={{ objectType: ObjectKind.Scenario, objectId: scenarioId! }}
          refetch={() => queryCache.invalidateQueries([QKeys.Audit])}
        />
      )}
      {isLinkControlsToScenarioModalOpen && (
        <LinkControlsToScenarioModal
          onClose={() => setLinkControlsToScenarioModal(null)}
          invalidateQueries={['scenario']}
          scenarioId={scenario.id}
          initialActiveTab={
            typeof isLinkControlsToScenarioModalOpen === 'object'
              ? isLinkControlsToScenarioModalOpen.activeTab
              : undefined
          }
          subTitle={{
            title: 'SCENARIO',
            value: scenario.name!,
          }}
        />
      )}
      {isDeleteModalOpen && (
        <ConfirmDeleteModal onConfirm={handleOnDeleteClick} onClose={() => setIsDeleteModalOpen(false)} />
      )}
      {isOpen && <ScenarioModal onClose={closeModal} invalidateQueries={['scenario']} />}
      {isVariablesModalOpen && (
        <VariablesWorkspaceModal
          onClose={() => setIsVariablesModalOpen(false)}
          scope={{ type: 'assessment', id: scenario.assessment.id, name: scenario.assessment.name }}
          readOnly={!!version}
          invalidateQueries={['scenario']}
        />
      )}
      {isExportPdfModalOpen && (
        <PdfExportModalScenario
          scenario={scenario}
          onClose={() => setIsExportPdfModalOpen(false)}
          isLogarithmic={isLogarithmic}
        />
      )}
      {isSandboxModalOpen && (
        <StyledSandboxModal isOpen onRequestClose={() => setIsSandboxModalOpen(false)} center={false}>
          <ScenarioView scenarioId={scenarioId!} />
        </StyledSandboxModal>
      )}
      <Div>
        <TitleWithBack gradient title="SCENARIO" />
        <CommonMenu
          configuredList={[
            !version && {
              type: 'EDIT',
              exe: (e) => onMenuClick(e, () => openModal(scenarioId)),
            },
            !version && {
              type: 'DELETE',
              exe: (e) => onMenuClick(e, () => setIsDeleteModalOpen(true)),
            },
            !version && {
              type: 'PDF',
              exe: (e) => onMenuClick(e, () => setIsExportPdfModalOpen(true)),
            },
          ]}
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleClose}
        />
        {!version && (
          <div className="head-end">
            <div className="head-end__controls">
              <QuickActionsMenu
                actions={[
                  {
                    type: 'ASSESSMENT_VARIABLES',
                    disabled: !!version,
                    onClick: () => {
                      setIsVariablesModalOpen(true);
                      mpEvent(MPEvents.ButtonClick, {
                        button: 'QuickActions-Assessment-Variables',
                        tags: ['SCENARIO'],
                      });
                    },
                  },
                  {
                    type: 'SUGGESTIONS',
                    disabled: !!version,
                    onClick: () =>
                      mpEvent(MPEvents.ButtonClick, { button: 'QuickActions-Suggestions', tags: ['SCENARIO', 'AI'] }),
                    itemProps: {
                      suggesterKeys: [scenario.id],
                    },
                    readonly: isSuggestionButtonReadOnly,
                  },
                  {
                    type: 'EVENT_ACTION',
                    disabled: !!version,
                    onClick: () => {
                      setIsCommentsModalOpen(true);
                      mpEvent(MPEvents.ButtonClick, { button: 'QuickActions-Comments', tags: ['SCENARIO'] });
                    },
                  },
                  {
                    type: 'SANDBOX',
                    readonly: !hasControls || incompleteState === 'TECH',
                    disabled: !!version,
                    onClick: () => {
                      setIsSandboxModalOpen(true);
                      mpEvent(MPEvents.ButtonClick, { button: 'QuickActions-Sandbox', tags: ['SCENARIO'] });
                    },
                    tooltip: projectedRiskTooltipText,
                  },
                ]}
              />
              <Settings
                active={!!anchorEl}
                onClick={(e) => {
                  e.stopPropagation();
                  setAnchorEl(e.currentTarget);
                }}
              />
            </div>
          </div>
        )}
      </Div>
      <Spacer $px={30} />
      <Theader>
        <div className="h-title-container">
          <Poppins px={32} weight={600} data-cy="scenario-name">
            {scenario.name}
          </Poppins>

          <div>
            <IncompleteStateMarkerNew scenario={scenario} place="right" />
          </div>
        </div>

        {!isSuggestionButtonReadOnly && <AiSuggest scenarioId={scenario.id} wrapperStyle={{ marginTop: 12 }} />}
        {version && (
          <>
            <Spacer $px={5} />
            <VersionHeader version={version} />
          </>
        )}
        <Spacer $px={30} />
        <div className="meta">
          <div className="sub-header">
            <InfoHint tooltipText={LABELS.scenarioStatus}>
              <DataTitle $noMargin>STATUS:</DataTitle>
            </InfoHint>
            {scenario.status ? (
              <DataValue data-cy="scenario-status">{getScenarioStatusName(scenario.status)}</DataValue>
            ) : (
              <NoData />
            )}
          </div>

          <div className="sub-header">
            <DataTitle $noMargin>RISK OWNER:</DataTitle>
            {scenario.riskOwner?.text ? (
              <DataValue data-cy="scenario-riskowner">{scenario.riskOwner.text}</DataValue>
            ) : (
              <NoData />
            )}
          </div>

          <div className="sub-header">
            <DataTitle $noMargin>REVIEW DUE:</DataTitle>
            {scenario.reviewDate ? (
              <DataValue>
                <TLLink isPast={isBefore(new Date(scenario.reviewDate), new Date())} date={scenario.reviewDate}>
                  {formatDate(scenario.reviewDate)}
                </TLLink>
              </DataValue>
            ) : (
              <NoData />
            )}
          </div>

          <div className="sub-header">
            <DataTitle $noMargin>EDITED:</DataTitle>
            {scenario.editedAt ? (
              <DataValue>
                <TLLink date={scenario.editedAt}>{formatDate(scenario.editedAt, { formatType: 'datetime' })}</TLLink>
              </DataValue>
            ) : (
              <NoData />
            )}
          </div>

          <div className="sub-header meta__loss">
            <DataTitle $noMargin>
              <InfoHint tooltipText={LABELS.expectedLoss} noIcon>
                EXPECTED LOSS:
              </InfoHint>
            </DataTitle>
            {expectedLossComponent}
          </div>
        </div>
      </Theader>
    </>
  );
};
